import { useEffect, useRef, type ReactNode } from 'react';
import type { BookingPublic } from '../api/types';
import { useZeitwahl } from '../provider';
import { formatInstantDay, formatTimeRange } from '../time/format';
import { BookingDetails } from './booking-details';

export interface ConfirmationProps {
  booking: BookingPublic;
  manageUrl?: string | null;
  /** Replace the default heading / body (e.g. the site's own copy). */
  title?: ReactNode;
  body?: ReactNode;
  /** Zone for the displayed time; defaults to the zone the visitor booked in. */
  timeZone?: string;
  locale?: string;
}

/** Final step: heading receives focus, shows the booked time, invite hint and manage link. */
export function Confirmation(props: ConfirmationProps): ReactNode {
  const { booking, manageUrl, title, body } = props;
  const { t, locale: contextLocale } = useZeitwahl();
  const locale = props.locale ?? contextLocale;
  const tz = props.timeZone ?? booking.timeZone;
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <section className="zw-confirmation">
      <svg
        className="zw-confirmation-icon"
        viewBox="0 0 24 24"
        width="36"
        height="36"
        aria-hidden="true"
        focusable="false"
      >
        <rect width="24" height="24" fill="currentColor" />
        <path
          d="M6.75 12.4 10.5 16.15 17.25 8.25"
          fill="none"
          strokeWidth="2.55"
          strokeLinecap="square"
          strokeLinejoin="miter"
        />
      </svg>
      <h2 ref={headingRef} className="zw-title" tabIndex={-1}>
        {title ?? t('confirmationTitle')}
      </h2>
      <p className="zw-confirmation-body">
        {body ??
          t('confirmationBody', {
            eventType: booking.eventType.title,
            date: formatInstantDay(booking.start, locale, tz),
            range: formatTimeRange(booking.start, booking.end, locale, tz),
            zone: tz,
          })}
      </p>
      <BookingDetails booking={booking} timeZone={tz} locale={locale} />
      {booking.location.url && (
        <a
          className="zw-button zw-button--secondary"
          href={booking.location.url}
          target="_blank"
          rel="noreferrer"
        >
          {t('confirmationJoin')}
        </a>
      )}
      <p className="zw-muted zw-confirmation-hint">
        {t('confirmationInvite', { email: booking.visitor.email })} {t('confirmationAddToCalendar')}
      </p>
      {manageUrl && (
        <p>
          <a className="zw-link" href={manageUrl}>
            {t('confirmationManage')}
          </a>
        </p>
      )}
    </section>
  );
}
